'use client';

import { AlertTriangle, Bird, Check, Crown, Sparkles, X } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { FooterClient } from '@/components/common/FooterClient';
import { ThemeAwareLogo } from '@/components/common/ThemeAwareLogo';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { createAuthClientAdapter } from '@/lib/auth/auth-client-adapter';
import { authClient } from '@/lib/auth-client';
export default function Pricing() {
  const [isLoading, setIsLoading] = useState(false);
  const {
    isAuthenticated
  } = useAuth();
  const availableFeatureIconClasses = 'h-4 w-4 text-success mt-0.5 flex-shrink-0';
  const unavailableFeatureIconClasses = 'h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0';
  const proFeatureIconClasses = 'h-4 w-4 text-primary mt-0.5 flex-shrink-0';
  const handleLogin = async () => {
    if (isAuthenticated) {
      return;
    }
    setIsLoading(true);
    try {
      const safeAuthClient = createAuthClientAdapter(authClient);
      await safeAuthClient.signInSocial({
        provider: 'twitter',
        callbackURL: '/timeline'
      });
    } catch {
      // Sign in errors are handled by the authClient internally
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-layer-chrome w-full bg-card/80 backdrop-blur-md border-b border-border/50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-2">
              <ThemeAwareLogo alt="Sweetistics" width={32} height={32} className="h-8 w-8 object-contain" />
              <span className="font-semibold text-lg">Sweetistics</span>
            </Link>
            <nav className="flex items-center gap-6">
              <Link href="/features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Features
              </Link>
              <Link href="/pricing" className="text-sm font-medium text-foreground transition-colors">
                Pricing
              </Link>
              {isAuthenticated ? <Button asChild variant="default" size="sm" className="font-medium">
                  <Link href="/timeline">Open Timeline</Link>
                </Button> : <Button onClick={() => {
              handleLogin().catch(() => {
                // handleLogin owns error reporting
              });
            }} variant="outline" size="sm" className="font-medium" disabled={isLoading}>
                  {isLoading ? 'Connecting...' : 'Login'}
                </Button>}
            </nav>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4" variant="secondary">
              Professional Twitter Analytics
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Choose Your Plan</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Unlock the full power of Twitter analytics with AI-powered insights and network optimization tools
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-12">
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Beta status</AlertTitle>
              <AlertDescription>
                Sweetistics is still beta and unfinished. Subscription purchasing is disabled while we validate the
                product experience.
              </AlertDescription>
            </Alert>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {/* Free Plan */}
            <Card className="relative">
              <CardHeader>
                <CardTitle className="text-2xl">Free</CardTitle>
                <CardDescription>Essential Twitter analytics for everyone</CardDescription>
                <div className="mt-4">
                  <span className="text-3xl font-bold">€0</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className={availableFeatureIconClasses} />
                    <span>Timeline sync & real-time updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className={availableFeatureIconClasses} />
                    <span>10 AI timeline reports per month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className={availableFeatureIconClasses} />
                    <span>Tweet archive management & search</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className={availableFeatureIconClasses} />
                    <span>Follower/following network analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className={availableFeatureIconClasses} />
                    <span>Public profile analytics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className={availableFeatureIconClasses} />
                    <span>Peak activity & posting patterns</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className={availableFeatureIconClasses} />
                    <span>Reply network visualization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className={availableFeatureIconClasses} />
                    <span>Basic engagement metrics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className={unavailableFeatureIconClasses} />
                    <span className="text-muted-foreground">Friendimizer™ network optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className={unavailableFeatureIconClasses} />
                    <span className="text-muted-foreground">Pulse profile monitoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className={unavailableFeatureIconClasses} />
                    <span className="text-muted-foreground">Extended AI reports</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className={unavailableFeatureIconClasses} />
                    <span className="text-muted-foreground">API access</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                {isAuthenticated ? <Button className="w-full" asChild>
                    <Link href="/timeline">Open Timeline</Link>
                  </Button> : <Button className="w-full" variant="outline" onClick={() => {
                handleLogin().catch(() => {
                  // handleLogin owns error reporting
                });
              }} disabled={isLoading}>
                    <Bird className="mr-2 h-4 w-4" />
                    {isLoading ? 'Connecting...' : 'Get Started Free'}
                  </Button>}
              </CardFooter>
            </Card>

            {/* Pro Plan */}
            <Card className="relative border border-primary/40 shadow-lg shadow-primary/20">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="px-3 py-1 shadow-sm">Most Popular</Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" />
                  Sweetistics Pro
                </CardTitle>
                <CardDescription>Professional Twitter analytics & AI insights</CardDescription>
                <div className="mt-4">
                  <span className="text-3xl font-bold">€49</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className={proFeatureIconClasses} />
                    <span className="font-medium">Everything in Free, plus:</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>GPT-5 AI analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>Extended & detailed AI reports</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>Friendimizer™ - Following optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>Pulse - Profile change monitoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>Custom date range analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>Priority processing queue</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>Advanced AI model selection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>API access for developers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>Data export capabilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className={proFeatureIconClasses} />
                    <span>Priority support</span>
                  </li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full" disabled>
                  Purchasing disabled during beta
                </Button>
              </CardFooter>
            </Card>
          </div>

          {/* Feature Comparison Table */}
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">Detailed Feature Comparison</h2>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4 font-medium">Feature</th>
                      <th className="text-center p-4 font-medium">Free</th>
                      <th className="text-center p-4 font-medium">Pro</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    <tr className="border-b">
                      <td className="p-4">AI Timeline Analysis</td>
                      <td className="text-center p-4">10/month</td>
                      <td className="text-center p-4 font-medium text-primary">Unlimited</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Report Length</td>
                      <td className="text-center p-4">Compact</td>
                      <td className="text-center p-4 font-medium text-primary">Extended</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Timeline Sync</td>
                      <td className="text-center p-4">
                        <Check className="h-4 w-4 text-success mx-auto" />
                      </td>
                      <td className="text-center p-4">
                        <Check className="h-4 w-4 text-primary mx-auto" />
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Network Analysis</td>
                      <td className="text-center p-4">Basic</td>
                      <td className="text-center p-4 font-medium text-primary">Advanced</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Friendimizer™</td>
                      <td className="text-center p-4">
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      </td>
                      <td className="text-center p-4">
                        <Check className="h-4 w-4 text-primary mx-auto" />
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Pulse Monitoring</td>
                      <td className="text-center p-4">
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      </td>
                      <td className="text-center p-4">
                        <Check className="h-4 w-4 text-primary mx-auto" />
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">API Access</td>
                      <td className="text-center p-4">
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      </td>
                      <td className="text-center p-4">
                        <Check className="h-4 w-4 text-primary mx-auto" />
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Priority Support</td>
                      <td className="text-center p-4">
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      </td>
                      <td className="text-center p-4">
                        <Check className="h-4 w-4 text-primary mx-auto" />
                      </td>
                    </tr>
                    <tr>
                      <td className="p-4">Processing Speed</td>
                      <td className="text-center p-4">Standard</td>
                      <td className="text-center p-4 font-medium text-primary">Priority</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto mt-16">
            <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Is Sweetistics available to purchase yet?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Not yet. Sweetistics is still beta and unfinished, and subscription purchasing is disabled while we
                    validate the product. We will announce billing once it opens.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Will I be able to cancel anytime?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Yes. Once subscriptions open, you will be able to cancel at any time and retain access through the
                    end of the current billing period.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Will refunds be available?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Once billing opens, we plan to offer a 14-day money-back guarantee for new Pro subscriptions. Please
                    see our{' '}
                    <Link href="/refund" className="text-primary hover:underline">
                      refund policy
                    </Link>{' '}
                    for details.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">What's the difference between AI report lengths?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Compact reports (Free) provide essential insights in 200-300 words. Normal reports (Pro) offer
                    comprehensive analysis in 500-800 words. Extended reports (Pro) deliver in-depth analysis with 1000+
                    words including detailed recommendations.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <FooterClient variant="static" isAuthenticated={isAuthenticated} />
    </div>;
}